import { type ConversationSummary, type DirectMessageEvent } from '@veriverse/shared';

/** Pure helpers for the direct-message state, kept out of the store so they are easy to test. */

export const THREAD_LIMIT = 200;

/** The other participant of a message, from `myId`'s point of view. */
export function otherParty(event: DirectMessageEvent, myId: string): string {
  return event.senderId === myId ? event.recipientId : event.senderId;
}

/**
 * Updates the conversation list for a message that was just sent or received. Unread only grows
 * for incoming messages in a conversation that is not currently open.
 */
export function applyDmToConversations(
  conversations: readonly ConversationSummary[],
  event: DirectMessageEvent,
  myId: string,
  openUserId: string | undefined,
  nameOf: (userId: string) => string,
): ConversationSummary[] {
  const other = otherParty(event, myId);
  const incoming = event.senderId !== myId;
  const existing = conversations.find((c) => c.userId === other);
  const updated: ConversationSummary = {
    userId: other,
    displayName: existing?.displayName ?? (incoming ? event.senderName : nameOf(other)),
    lastBody: event.body,
    lastAt: event.at,
    unread: (existing?.unread ?? 0) + (incoming && openUserId !== other ? 1 : 0),
    blocked: existing?.blocked ?? false,
  };
  return [updated, ...conversations.filter((c) => c.userId !== other)].sort(
    (a, b) => b.lastAt - a.lastAt,
  );
}

/** Appends to a thread, ignoring duplicates (the echo of our own message) and capping its size. */
export function appendToThread(
  thread: readonly DirectMessageEvent[],
  event: DirectMessageEvent,
): DirectMessageEvent[] {
  if (thread.some((m) => m.id === event.id)) return [...thread];
  return [...thread, event].slice(-THREAD_LIMIT);
}

export function totalUnread(conversations: readonly ConversationSummary[]): number {
  return conversations.reduce((sum, c) => sum + (c.blocked ? 0 : c.unread), 0);
}
